import type { PurchaseSource } from '@sunflower-games/analytics-events';

import { postRequest } from '../services/api';
import { logToNewRelic } from '../services/debug';
import { isPurchaseLimitError } from '../services/payments';
import type { PlatformEnum } from '../services/platformContext/platformContextTypes';
import type { IStorePackage, EventTriggerPurchaseData } from '../stores/app/types';
import { EnumPaymentMethods } from '../stores/payredeem/types';

import { generateGuid } from './helpers';

export async function skrillMakePurchase(args: {
  gameId?: string;
  packageId: IStorePackage['packageId'];
  platform: PlatformEnum;
  eventTriggerData?: EventTriggerPurchaseData;
  purchaseSource?: PurchaseSource;
  onSuccess: (href?: string, transactionId?: string, isFirstDeposit?: boolean) => void;
  onPurchaseLimitExceeded?: () => Promise<any> | void;
  onKYCRequired: () => Promise<any> | void;
  onFail: () => Promise<any> | void;
}) {
  try {
    const generatedTransactionId = generateGuid();
    const paymentRequest = {
      packageId: args.packageId,
      purchaseSource: args.purchaseSource,
      paymentMethod: EnumPaymentMethods.skrill,
      ...(args.platform ? { platform: args.platform } : {}),
      merchantRefNum: generatedTransactionId,
      ...args.eventTriggerData,
    };

    const hrefOrigin = location.origin;
    const gameIdParams = args.gameId ? `&gameId=${args.gameId}` : '';

    const data = {
      ...paymentRequest,
      links: [
        {
          rel: 'default',
          href: `${hrefOrigin}/?method=skrill&status=default&type=purchase`,
          method: 'GET',
        },
        {
          rel: 'on_completed',
          href: `${hrefOrigin}/?method=skrill&status=complete&type=purchase&packageId=${args.packageId}${gameIdParams}`,
          method: 'GET',
        },
        {
          rel: 'on_failed',
          href: `${hrefOrigin}/?method=skrill&status=faile&type=purchase`,
          method: 'GET',
        },
        {
          rel: 'on_cancelled',
          href: `${hrefOrigin}/?method=skrill&status=cancelled&type=purchase`,
          method: 'GET',
        },
      ],
    };

    const response = await postRequest<{
      data?: {
        success: boolean;
        details?: { links: { href: string }[]; transactionId: string; isFirstDeposit: boolean };
      };
    }>('user/create-payment-paysafe', data);

    logToNewRelic(
      'sendSuccessPurchaseEvent',
      {
        message: 'user/create-payment-paysafe',
        context: {
          details: response.data?.details,
        },
      },
      'info',
    );

    if (response.data?.success) {
      const href = response?.data?.details?.links[0].href;
      const { transactionId: responseTransactionId, isFirstDeposit } = response.data.details ?? {};
      args.onSuccess(href, responseTransactionId || generatedTransactionId, isFirstDeposit);
    }
  } catch (error: any) {
    logToNewRelic(
      'External Payments + Skrill',
      JSON.stringify(
        {
          error,
          message: error.message,
        },
        null,
        2,
      ),
      'error',
    );

    if (typeof args.onPurchaseLimitExceeded === 'function' && isPurchaseLimitError(error)) {
      return args.onPurchaseLimitExceeded();
    }

    const errorName = error.response?.data?.errors[0]?.name || error.name;

    if (errorName === 'ForceKYCError') {
      await args.onKYCRequired();
      return;
    }

    await args.onFail();
  }
}
